/**
 * Admin V3 — Overview module — "Aperçu des réservations" card (Row 2 left)
 *
 * Shows today/week/month/total booking counts + today/total revenue.
 * Explicit error state per owner directive (Phase 2.1 decision #5).
 *
 * Data source: GET /stats/dashboard
 *   - data.bookings.{today, week, month, total}
 *   - data.revenue.{today, total}
 */

import React from 'react';
import { Calendar, AlertCircle, Clock } from 'lucide-react';
import { SectionCard, EmptyState, currency } from '../../shared/visualTokens';
import { t } from '../../i18n';

const StatRow = ({ label, value, valueClass = 'text-slate-900' }) => (
  <div className="flex items-center justify-between border-b border-slate-100 py-2.5 last:border-0">
    <span className="text-sm text-slate-500">{label}</span>
    <span className={`text-sm font-semibold ${valueClass}`}>{value ?? '—'}</span>
  </div>
);

const OverviewBookingsCard = ({ dashboard, locale = 'fr' }) => {
  const { data, loading, error } = dashboard;

  if (loading) {
    return (
      <SectionCard
        title={t('overview.bookings.title', locale)}
        icon={Calendar}
        iconClass="bg-blue-100 text-blue-600"
      >
        <EmptyState icon={Clock} title={t('overview.empty', locale)} />
      </SectionCard>
    );
  }

  if (error || !data) {
    return (
      <SectionCard
        title={t('overview.bookings.title', locale)}
        icon={Calendar}
        iconClass="bg-blue-100 text-blue-600"
      >
        <EmptyState
          icon={AlertCircle}
          title={t('overview.error', locale)}
          desc={error || undefined}
        />
      </SectionCard>
    );
  }

  const b = data.bookings || {};
  const r = data.revenue || {};

  return (
    <SectionCard
      title={t('overview.bookings.title', locale)}
      icon={Calendar}
      iconClass="bg-blue-100 text-blue-600"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
        <div>
          <StatRow label={t('overview.bookings.today', locale)} value={b.today} />
          <StatRow label={t('overview.bookings.week', locale)}  value={b.week} />
          <StatRow label={t('overview.bookings.month', locale)} value={b.month} />
          <StatRow label={t('overview.bookings.total', locale)} value={b.total} />
        </div>
        <div>
          <StatRow
            label={`${t('overview.bookings.revenueLabel', locale)} — ${t('overview.bookings.today', locale)}`}
            value={r.today != null ? currency(r.today) : null}
            valueClass="text-emerald-600"
          />
          <StatRow
            label={`${t('overview.bookings.revenueLabel', locale)} — ${t('overview.bookings.total', locale)}`}
            value={r.total != null ? currency(r.total) : null}
            valueClass="text-emerald-600"
          />
        </div>
      </div>
    </SectionCard>
  );
};

export default OverviewBookingsCard;
